import { cn } from '@/app/utils'
import { ReactNode, useEffect, useRef, useState } from 'react'

export type Options = {
  id: string
  content: any
}

type Props = {
  children: ReactNode
  options: Options[]
  className?: string
  onSelect?: any
  selected: Options
  popOverClassName?: string
  size?: 'large' | 'small'
  containerClassName?: string
}

function doesContain(target: HTMLElement, container: HTMLElement) {
  if (target === null) return false

  if (target.parentNode === container) {
    return true
  }

  return doesContain(target.parentNode as HTMLElement, container)
}

export function DropdownMenu({
  children,
  options,
  className,
  selected,
  onSelect,
  popOverClassName,
  size = 'small',
  containerClassName,
}: Props) {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const ref = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside: EventListenerOrEventListenerObject = (
      e: Event,
    ) => {
      if (
        isOpen &&
        ref.current &&
        !doesContain(e.target as HTMLElement, ref.current) &&
        buttonRef.current !== e.target
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('click', handleClickOutside, true)

    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [isOpen])

  const toggleDropdown = () => {
    setIsOpen(!isOpen)
  }

  return (
    <div className={cn(`relative h-60 w-full font-medium`, containerClassName)}>
      <div
        aria-hidden
        ref={buttonRef}
        onClick={toggleDropdown}
        className={cn(
          'c-b4 flex !h-48 w-full cursor-pointer items-center justify-between rounded-2xl px-20 py-13 text-txt-secondary',
          { '!c-b3 !h-60 !py-17': size === 'large' },
          { 'text-[#404040]': isOpen && selected },
          { 'text-french-bleu': !isOpen && selected },
          `${className}`,
        )}>
        {selected ? selected.content : children}

        <Arrow isOpen={isOpen} size={size} />
      </div>
      <div
        ref={ref}
        className={cn(
          'relative z-10 mt-4 overflow-y-auto rounded-popover bg-white',
          isOpen
            ? `border-1 border-[#e5e5e5] p-12 xl:shadow-md`
            : 'max-h-0 !border-0',
          popOverClassName,
        )}>
        {options.map((item) => (
          <div
            aria-hidden
            className={cn(
              'c-b3 cursor-pointer rounded-lg px-12 py-8 transition-all hover:bg-french-bleu/10',
              { 'text-french-bleu': selected.id === item.id },
              popOverClassName,
            )}
            id={String(item.id)}
            onClick={() => {
              onSelect(item)
              setIsOpen(false)
            }}
            key={item.id}>
            <div className="flex items-center justify-between">
              {item.content}
              {selected.id === item.id && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M17.2559 5.24408C17.5814 5.56951 17.5814 6.09715 17.2559 6.42259L10.1011 13.5774C9.12479 14.5537 7.54187 14.5537 6.56556 13.5774L3.57741 10.5893C3.25197 10.2638 3.25197 9.73618 3.57741 9.41074C3.90284 9.08531 4.43048 9.08531 4.75592 9.41074L7.74407 12.3989C8.06951 12.7243 8.59715 12.7243 8.92258 12.3989L16.0774 5.24408C16.4028 4.91864 16.9305 4.91864 17.2559 5.24408Z"
                    fill="#3366B6"
                  />
                </svg>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function Arrow(props: { isOpen: boolean; size: 'large' | 'small' }) {
  return (
    <div
      className={cn(
        'rotate-0 duration-300',
        { 'rotate-180 duration-300': props.isOpen },
        { '[&_svg]:!size-16': props.size === 'small' },
      )}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill={'#A3A3A3'}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4.79289 8.54289C5.18342 8.15237 5.81658 8.15237 6.20711 8.54289L12 14.3358L17.7929 8.54289C18.1834 8.15237 18.8166 8.15237 19.2071 8.54289C19.5976 8.93341 19.5976 9.56658 19.2071 9.9571L13.4142 15.75C12.6332 16.531 11.3668 16.531 10.5858 15.75L4.79289 9.95711C4.40237 9.56658 4.40237 8.93342 4.79289 8.54289Z"
          fill={'#A3A3A3'}
        />
      </svg>
    </div>
  )
}
